import { useEffect, useState, useMemo } from 'react'

import {
  GoogleCoordinates,
  GoogleMapsStructuredPlace
} from '../interfaces/googleMapsInterfaces'
import CoincidencesList from './CoincidencesList'
import DefaultSearches from './DefaultSearches'
import MyLocation from './MyLocation'
import PopularSearches from './PopularSearches'
import SavedSearches from './SavedSearches'
import { TiDeleteOutline } from "react-icons/ti";
import { ILocation } from '@monopolio-ui/state-library/stores/filterStore/types'

export interface Props {
  /**
   * This property indicates whether the Autocomplete is focused.
   * @type {boolean}
   * @default false
   */
  isFocusedAutocomplete: boolean

  /**
   * This is the className of the Container
   * @type {string}
   * */
  className?: string

  /**
   * This is the className of the Container
   * @type {string}
   * */
  itemClassName?: string

  /**
   * This is the searchText of the SearchField
   * @type {string}
   * */
  searchText: string

  /**
   * This function is called when the match is selected
   * @type {(place: GoogleMapsStructuredPlace) => void}
   * */
  onSelected: (place: GoogleMapsStructuredPlace) => void

  /**
   * This is the myLocation of the AutocompleteResults
   * @type {boolean}
   * @default false
   */
  enableMyLocation?: boolean

  /**
   * This is the defaultSearches of the AutocompleteResults
   * @type {boolean}
   * @default false
   */
  enableDefaultSearches?: boolean

  /**
   * This is the popularSearches of the AutocompleteResults
   * @type {boolean}
   * @default false
   */
  enablePopularSearches?: boolean

  /**
   * This is the savedSearches of the AutocompleteResults
   * @type {boolean}
   * @default false
   */
  enableSavedSearches?: boolean

  /**
   * This function is called when the matches are loaded.
   * @type {(hasCoincidences: boolean) => void}
   * */
  onLoadedCoincidences?: (hasCoincidences: boolean) => void

  /**
   * Indicates whether the street number must be validated.
   * @type {boolean}
   */
  validateStreetNumber?: boolean

  /**
   * Assigns default limits.
   * @type {google.maps.CircleLiteral}
   */
  defaultBounds?: google.maps.CircleLiteral

  /**
   * Assigns default country.
   * @type {string}
   */
  limitedCountry?: string

  /**
   * Assigns default regions.
   * @type {string[]}
   */
  limitedRegions?: string[]

  /**
   * Assign default limits, this overrides the "defaultBounds".
   * @type {{sw:GoogleCoordinate,ne:GoogleCoordinate}}
   */
  limitedBounds?: {
    sw: GoogleCoordinates
    ne: GoogleCoordinates
  }

  /**
   * This is the types of the AutocompleteResults
   * @type {string[]}
   * @default ["geocode"]
   */
  placeTypes?: string[]

  /**
   * Boolean to inidicate if is necesary remark the option selected in the coincidences list
   * @type {boolean}
   */
  withRemarkSelected?: boolean

  /**
   * Styles for location's label.
   * @type {string}
   */
  labelClassName?: string

  /**
   * Styles for location's label icon.
   * @type {string}
   */
  iconLabelClassName?: string

  /**
   * @description locations to show in the results
   * @type {ILocation[]}
   */
  locations?: ILocation[]

  /**
   * @description This function is called when the locations delete button is clicked
   * @type {(id:string) => void}
   */
  onDeleteLocation?: (id:string) => void
    
  remarkSelectedClassNames?: string

  focusOptionClassNames?: string

  itemTextClassName?: string
  inputWidth: number
}

/**
 * This component is responsible for displaying the matches of the search and default suggestions.
 * @param isFocusedAutocomplete - This property indicates whether the Autocomplete is focused.
 * @param className - This is the className of the Container.
 * @param itemClassName - This is the className of the item.
 * @param searchText - This is the searchText of the SearchField.
 * @param onSelected - This function is called when the match is selected.
 * @param visibledSeccions - This is the visibledSeccions of the AutocompleteResults.
 * @param enableMyLocation - This is the myLocation of the AutocompleteResults.
 * @param enableDefaultSearches - This is the defaultSearches of the AutocompleteResults.
 * @param enablePopularSearches - This is the popularSearches of the AutocompleteResults.
 * @param enableSavedSearches - This is the savedSearches of the AutocompleteResults.
 * @param onLoadedCoincidences - This function is called when the matches are loaded.
 * @param validateStreetNumber - Indicates whether the street number must be validated.
 * @param defaultBounds - Assigns default limits.
 * @param limitedCountry - Assigns default country, default "MX".
 * @param limitedRegions - Assigns default regions.
 * @param limitedBounds - Assign default limits, this overrides the "defaultBounds".
 * @param placeTypes - This is the types of the AutocompleteResults.
 * @param withRemarkSelected - boolean to remark a selected option with the searchText.
 * @param locations - locations to show in the results
 * @param onDeleteLocation - This function is called when the locations delete button is clicked
 * @returns {JSX.Element}
 * */
const ResultsContainer = ({
  isFocusedAutocomplete,
  className,
  itemClassName,
  searchText,
  onSelected,
  onDeleteLocation,
  enableMyLocation = false,
  enableDefaultSearches = false,
  enablePopularSearches = false,
  enableSavedSearches = false,
  onLoadedCoincidences: triggerOnLoadedCoincidences,
  validateStreetNumber,
  defaultBounds,
  limitedCountry,
  limitedRegions,
  limitedBounds,
  placeTypes,
  withRemarkSelected,
  labelClassName,
  iconLabelClassName,
  remarkSelectedClassNames,
  focusOptionClassNames,
  itemTextClassName,
  inputWidth,
  locations
}: Props): JSX.Element => {
  const [isInitialized, setIsInitialized] = useState(false)
  const [hasCoincidences, setHasCoincidences] = useState(false)

  const hasAnySectionEnabled = useMemo(() => {
    return (
      enableMyLocation ||
      enableDefaultSearches ||
      enablePopularSearches ||
      enableSavedSearches
    )
  }, [
    enableMyLocation,
    enableDefaultSearches,
    enablePopularSearches,
    enableSavedSearches
  ])

  const isVisibledResultsContainer = useMemo(() => {
    if (!isFocusedAutocomplete) {
      return false
    }

    if (hasAnySectionEnabled) {
      return true
    }

    if (searchText) {
      return true
    } 

    return false
  }, [isFocusedAutocomplete, hasAnySectionEnabled, searchText])

  /**
   * This function is called when the matches are loaded.
   * @param {boolean} coincidences - Indicates whether there are matches.
   * @returns {void}
   * */
  const onLoadedCoincidences = (coincidences: boolean): void => {
    setHasCoincidences(coincidences)

    setIsInitialized(true)

    if (!searchText) {
      return
    }

    if (triggerOnLoadedCoincidences) {
      triggerOnLoadedCoincidences(coincidences)
    }
  }

  useEffect(() => {
    if (!searchText) {
      setIsInitialized(false)
    }
  }, [searchText])

  return (
    <div hidden={!isVisibledResultsContainer}>
      <div
        className={`absolute ${
          hasAnySectionEnabled || (!hasAnySectionEnabled && isInitialized)
            ? 'p-4'
            : ''
        } flex flex-col gap-4 bg-white rounded-lg [box-shadow:0px_3px_14px_-3px_rgba(24,26,61,0.12)] ${className}`}
        style={{ width: `${inputWidth}px` }}
      >
        {
          locations && locations.length > 0 &&
          <div className='flex flex-wrap gap-2'>
            {
              locations.map(({id, searchAddress}) => (
                <div key={id} className="flex items-center px-2 py-1 rounded-full bg-[#F4F7FA] gap-1 w-[105px]">
                  <p className='max-w-[69px] truncate text-[10px] font-bold uppercase'>{searchAddress}</p>
                  <button type='button' onClick={() => onDeleteLocation?.(id)} className="w-4 h-4 text-text-primary">
                    <TiDeleteOutline className='text-base' />
                  </button>
                </div>
              ))
            }
          </div>
        }
        {enableMyLocation ? (
          <div
            className={`
           ${
             enableDefaultSearches ||
             enablePopularSearches ||
             enableSavedSearches ||
             (isInitialized && !hasCoincidences)
               ? 'pb-2 border-b border-[#EBEDF0]'
               : ''
           }
          `}
            hidden={hasCoincidences && isInitialized}
          >
            <MyLocation
              isVisible={
                !hasCoincidences && Boolean(isVisibledResultsContainer)
              }
              onSelected={onSelected}
              labelClassName={labelClassName}
              iconLabelClassName={iconLabelClassName}
            />
          </div>
        ) : null}

        <div hidden={!isInitialized}>
          <CoincidencesList
            focusOptionClassNames={focusOptionClassNames}
            remarkSelectedClassNames={remarkSelectedClassNames}
            withRemarkSelected={withRemarkSelected}
            itemClassName={itemClassName}
            itemTextClassName={itemTextClassName}
            isVisible={isInitialized && Boolean(isVisibledResultsContainer)}
            searchText={searchText}
            onSelected={onSelected}
            onLoadedCoincidences={onLoadedCoincidences}
            validateStreetNumber={validateStreetNumber}
            defaultBounds={defaultBounds}
            limitedCountry={limitedCountry}
            limitedRegions={limitedRegions}
            limitedBounds={limitedBounds}
            placeTypes={placeTypes}
          />
        </div>

        {enableDefaultSearches ? (
          <div hidden={Boolean(searchText) && isInitialized}>
            <DefaultSearches />
          </div>
        ) : null}

        {enablePopularSearches ? (
          <div hidden={Boolean(searchText) && isInitialized}>
            <PopularSearches />
          </div>
        ) : null}

        {enableSavedSearches ? (
          <div hidden={Boolean(searchText) && isInitialized}>
            <SavedSearches />
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default ResultsContainer
